'use client'

import { cn } from '@/lib/utils'

import { EASE, HAIRLINE, SectionHead, useReveal } from '@/components/page-kit'
import { ACCOUNTS_MODULES } from './data'

/* The T-account. A single rule descends from the block's top border and every
   module sits hard against it: name to the left, what it does to the right.
   The shared template renders these as six identical chips in a 3-column grid.

   The rule is one absolutely-positioned hairline rather than a per-row cell, so
   it stays unbroken where it crosses the row rules — which is the crossing that
   makes the whole thing read as ledger ruling. */

export function AccountsLedger() {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="The ledger"
          title="From ledger management to GST compliance."
        />

        <div
          ref={list.ref}
          className={cn('relative mt-10 border-t sm:mt-12', HAIRLINE)}
        >
          {/* The descending rule — lg only, where there are two columns to
              divide. It stops short of the last row's baseline so the block
              closes rather than trailing off. */}
          <span
            aria-hidden
            className="absolute bottom-0 left-1/2 top-0 hidden w-px origin-top bg-border/70 lg:block"
            style={{
              transform: list.shown ? 'scaleY(1)' : 'scaleY(0)',
              transition: `transform 900ms ${EASE} 80ms`,
            }}
          />

          {ACCOUNTS_MODULES.map((m, i) => (
            <div
              key={m.name}
              className={cn(
                'grid grid-cols-1 gap-1 border-b py-4 sm:py-5',
                'lg:grid-cols-2 lg:items-baseline lg:gap-0',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(8px)',
                transition: `opacity 480ms ${EASE} ${i * 70}ms, transform 480ms ${EASE} ${i * 70}ms`,
              }}
            >
              {/* Code column hard left, name right against the rule — the
                  column order a trial balance uses. */}
              <div className="flex items-baseline gap-4 lg:justify-between lg:pr-8">
                <span className="w-6 shrink-0 text-[11px] tabular-nums text-muted-foreground/60">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-heading text-base font-normal leading-snug text-foreground sm:text-lg lg:text-right">
                  {m.name}
                </h3>
              </div>

              <p className="pl-10 text-[13px] leading-snug text-muted-foreground sm:text-sm lg:pl-8">
                {m.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
